/**
 * createStrategyFlow — one entry point for "create objective" UI actions.
 *
 * Canon (docs/1-canon/01-terminology-and-glossary.md, "## Strategy" /
 * "## Strategic Objectives"): a Strategy is the parent container; Strategic
 * Objectives "belong to a Strategy (`strategyId`)" and are the canonical
 * cross-cutting alignment object — the table is `strategic_objectives`.
 *
 * Before this file, five UI entry points (ObjectivesPage, StrategyDetail,
 * StrategyCanvasEditor, StrategyAlignment, OstRootLinkModal) each called
 * `strategyAlignment.ts`'s `createObjective` directly — same table, same
 * shape, five independent call sites with their own try/catch. Route new
 * "create objective" UI through this module instead of importing
 * `createObjective` a sixth time.
 *
 * Two OTHER tables also get written from "create objective/goal"-shaped UI,
 * and this module deliberately does NOT route them here, because they are
 * not the same domain object per canon:
 *
 * - `strategy_proximate_objectives` (`strategyKernelStore.ts`'s own
 *   `createObjective`, used by `ProximateObjectivesTree.tsx`) — "Proximate
 *   Objectives" is a `strategy-kernel`-scoped hierarchy (kernel_id,
 *   parent_id, level, org_unit_id) documented only in the concept register
 *   (`docs/3-system/00-concept-registers/strategy.yaml`, status `draft`),
 *   not in canon 01/03. Forcing it onto `strategic_objectives` would drop
 *   its kernel scoping and hierarchy fields — a schema change, not a UI
 *   routing fix, and out of scope for a no-migrations job.
 * - `goals` (`strategyAlignment.ts`'s `createGoal`, used by `GoalsPage.tsx`)
 *   — "Goal" has no glossary entry at all; it carries metric-tracking fields
 *   (`metricName`, `targetValue`, `currentValue`, `unit`) that
 *   `strategic_objectives` has no columns for. Same reasoning: collapsing it
 *   here would delete a live feature's data shape, not fix a duplicate.
 *
 * If Proximate Objectives and/or Goals should really be unified with
 * Strategic Objectives, that's a canon ruling + migration, not a client
 * routing change — flag it instead of guessing here.
 */

import { createObjective as createStrategicObjectiveRow, type StrategicObjective } from '../strategyAlignment';

export type { StrategicObjective };

/**
 * Create a Strategic Objective (canon 01 "## Strategic Objectives") under a
 * Strategy. Throws with the server's real error message on failure — never
 * swallow it into a generic "failed to save".
 */
export async function createStrategicObjective(
  input: Omit<StrategicObjective, 'id' | 'createdAt' | 'updatedAt' | 'weight'>,
  userId: string,
): Promise<StrategicObjective> {
  return createStrategicObjectiveRow(input, userId);
}
